import { useEffect, useRef, useState } from "react";

import style from "./TxBoxView.module.scss";

import { TxInfo } from "@terra-money/terra.js";
import classNames from "classnames/bind";
import { useHistory } from "react-router-dom";
import {
    CreateTxFailed,
    Timeout,
    TxFailed,
    TxUnspecifiedError,
    UserDenied,
} from "@terra-money/wallet-provider";
import TxErrorView from "./TxErrorView";
import TxDoneView from "./TxDoneView";
import TxLoadingView from "./TxLoadingView";
import TxPendingView from "./TxPendingView";

const cx = classNames.bind(style);

enum TxBoxViewStatus {
    Before,
    Pending,

    NetworkError,
    UserDenied,
    Loading,
    Done,

    Timeout,
    Error,
}

export default function TxBoxView(props: {
    children: any;
    hash?: HashResult;
    clearHash: () => void;
    transparent?: boolean;
}) {
    const history = useHistory();
    const [step, setStep] = useState(TxBoxViewStatus.Before);
    const [txInfo, setTxInfo] = useState<TxInfo | undefined>(undefined);

    const errorMessage = useRef("");

    useEffect(() => {
        if (props.hash && props.hash.isPending) {
            setStep(TxBoxViewStatus.Pending);
            return;
        }

        if (props.hash && (props.hash.response || props.hash.error)) {
            if (!txInfo) {
                //before get txInfo

                if (!props.hash.error) {
                    setStep(TxBoxViewStatus.Loading);
                } else {
                    if (props.hash.error instanceof UserDenied) {
                        props.clearHash();
                    } else if (
                        props.hash.error instanceof CreateTxFailed ||
                        props.hash.error instanceof TxFailed ||
                        props.hash.error instanceof Timeout ||
                        props.hash.error instanceof TxUnspecifiedError
                    ) {
                        errorMessage.current = props.hash.error.message;
                        setStep(TxBoxViewStatus.Error);
                    } else {
                        setStep(TxBoxViewStatus.NetworkError);
                    }
                }
            } else {
                //after get txInfo
                if (txInfo.code) {
                    errorMessage.current = txInfo.raw_log;
                    setStep(TxBoxViewStatus.Error);
                } else {
                    setStep(TxBoxViewStatus.Done);
                }
            }
        } else {
            setTxInfo(undefined);
            setStep(TxBoxViewStatus.Before);
        }
    }, [props, txInfo]);

    function confirmPressed(success: boolean) {
        if (props.hash && props.hash.callback) {
            props.hash.callback(success);
        }

        setTxInfo(undefined);
        setStep(TxBoxViewStatus.Before);
        props.clearHash();
    }

    return (
        <div
            className={cx(style.container, {
                transparent: !props.hash && props.transparent,
            })}
        >
            {step === TxBoxViewStatus.Pending ? (
                <TxPendingView />
            ) : step === TxBoxViewStatus.Loading ? (
                <TxLoadingView
                    result={props.hash!}
                    txFound={(info: TxInfo) => {
                        setTxInfo(info);
                    }}
                    timeout={() => {
                        setStep(TxBoxViewStatus.Timeout);
                    }}
                />
            ) : step === TxBoxViewStatus.Done ? (
                <TxDoneView
                    result={props.hash!}
                    txInfo={txInfo!}
                    pressed={() => {
                        if (props.hash!.redirect) {
                            history.push(props.hash!.redirect);
                        } else {
                            confirmPressed(true);
                        }
                    }}
                />
            ) : step === TxBoxViewStatus.Timeout ? (
                <TxErrorView
                    message="timeout"
                    pressed={() => {
                        confirmPressed(false);
                    }}
                />
            ) : step === TxBoxViewStatus.UserDenied ? (
                <TxErrorView
                    message="User Denied."
                    pressed={() => {
                        confirmPressed(false);
                    }}
                />
            ) : step === TxBoxViewStatus.NetworkError ? (
                <TxErrorView
                    message="Check your network."
                    pressed={() => {
                        confirmPressed(false);
                    }}
                />
            ) : step === TxBoxViewStatus.Error ? (
                <TxErrorView
                    message={errorMessage.current}
                    pressed={() => {
                        confirmPressed(false);
                    }}
                />
            ) : (
                props.children
            )}
        </div>
    );
}
